/**
 * wristSeatUnderPin — the wrist pin is a SURFACE point (a click lands on the
 * skin), so it cannot be the joint; but it says exactly where ACROSS the
 * wrist the person means, and that answer was being thrown away: the station
 * move used only the click's along-arm coordinate, and the lateral seat was
 * measured by the elbow's cylinder capture — 0.45 × forearm length in radius,
 * which at a wrist swallows the palm and the thumb, so the "wrist centre" it
 * found was the centroid of hand mass (Allen, 2026-08-26: "the wrist joint
 * ends up directly above the thumb… we seem to be ignoring that user input").
 *
 * This seat honours the pin in two coordinates and measures the third. In
 * the station's plane the wrist's cross-section is an ellipse — wide across
 * (radial↔ulnar), thin through (dorsal↔palmar); its principal axes come from
 * the slab's own covariance. From the pin, cast along the THIN axis into the
 * tube at the pin's lateral position; the far skin along that chord is the
 * other wall; the joint sits at the chord's midpoint — beneath the click,
 * half a wrist deep, at the lateral position the person chose. The thumb
 * enters only if it lies on that chord, which a dorsal or palmar click never
 * crosses. A click on the wrist's SIDE (beyond 70% of the half-width) has no
 * meaningful thin chord, so it casts toward the section's centroid instead.
 *
 * Refuses when the pin is not on the skin of this slab, or the chord finds
 * no far wall — §8: refuse, never guess. Depends on: math/vec3. Depended on
 * by: the Document driver's wrist station.
 */
import { vec3Add, vec3Dot, vec3Length, vec3Scale, vec3Sub, type Vec3Data } from '../math/vec3.js';
import { WristStationRefusal } from './wrist-station.js';

/**
 * How many of the slab points nearest the seed the adaptive link measures its
 * spacing over. The flood only has to START, so local density is what matters;
 * scanning the whole slab would also cost O(n²) on a whole-scene section.
 */
export const LINK_SPACING_SAMPLE = 24;
/**
 * The adaptive link is this many times the ring's own 90th-percentile vertex
 * gap. The requirement the constant's note states is that the link EXCEED the
 * ring's spacing; 1.5 clears a p90 gap without reaching for the next limb.
 */
export const LINK_SPACING_FACTOR = 1.5;
/**
 * The adaptive link never exceeds this multiple of the configured one. The
 * connected-cluster rule exists because the slab plane is infinite and an
 * A-pose thigh can sit in it; an unbounded link would bridge to it and seat
 * the wrist in someone's leg.
 */
export const LINK_ADAPT_CAP = 1.75;

/**
 * The link distance to flood the ring with — the configured one, raised to the
 * ring's own local spacing when that is coarser, capped.
 *
 * WHY THIS IS NOT JUST `0.20 · limbLength`. The driver sizes the link as a
 * fraction of the forearm, but a mesh's vertex spacing does NOT scale with the
 * limb: a short forearm gets a proportionally smaller link over a ring whose
 * gaps stayed the same size, and the cluster collapses to the seed. MEASURED
 * 2026-09-07 on Gilda (91 mm forearm, 18.3 mm link, ring gaps to 20.7 mm): the
 * left ring connected 8 vertices and the right 5, against the 12 a section
 * needs, and the Hand Studio refused both hands. With this rule they hold 35
 * and 23. On healthy specimens it changes nothing — Harry, Joan and Vera have
 * 4.5–8.3 mm spacing on 124–140 mm forearms, so the configured link already
 * exceeds 1.5× it and `max` keeps it (65/65, 47/47, 42/42 vertices, unchanged).
 *
 * Duplicate vertices (UV and normal splits, spacing 0) are skipped: they are an
 * export artifact and say nothing about the ring's gaps.
 */
export function adaptiveLinkDistance(inPlane: readonly Vec3Data[], seedIndex: number, configured: number): number {
  const seed = inPlane[seedIndex];
  if (seed === undefined) return configured;
  const nearest = inPlane
    .map((p) => ({ p, d: vec3Length(vec3Sub(p, seed)) }))
    .sort((a, b) => a.d - b.d)
    .slice(0, LINK_SPACING_SAMPLE)
    .map((x) => x.p);
  if (nearest.length < 4) return configured;
  const gaps: number[] = [];
  for (let i = 0; i < nearest.length; i += 1) {
    let closest = Infinity;
    for (let j = 0; j < nearest.length; j += 1) {
      if (i === j) continue;
      const d = vec3Length(vec3Sub(nearest[i] as Vec3Data, nearest[j] as Vec3Data));
      if (d > 1e-6 && d < closest) closest = d;
    }
    if (Number.isFinite(closest)) gaps.push(closest);
  }
  if (gaps.length === 0) return configured;
  gaps.sort((a, b) => a - b);
  const p90 = gaps[Math.min(gaps.length - 1, Math.floor(gaps.length * 0.9))] as number;
  return Math.min(Math.max(configured, LINK_SPACING_FACTOR * p90), LINK_ADAPT_CAP * configured);
}

export interface WristSeatUnderPin {
  /** The joint's seat: beneath the pin at the tube's mid-depth, in the station's plane. */
  readonly position: Vec3Data;
  /** The chord's length through the tube along the cast (world units). */
  readonly depth: number;
  /** Vertices that defined the far wall (within the slab and the chord's lateral tolerance). */
  readonly sampled: number;
  /** 'thin-axis' for a dorsal/palmar click, 'toward-centroid' for a side click. */
  readonly cast: 'thin-axis' | 'toward-centroid';
  /** The link the ring was actually flooded with — the configured one, or the spacing-adapted one. */
  readonly linkUsed: number;
}

export function wristSeatUnderPin(
  vertices: readonly Vec3Data[],
  /** The station point ON the limb axis (the along-arm move already applied). */
  station: Vec3Data,
  /** The limb axis, any length, pointing from the forearm toward the hand. */
  axis: Vec3Data,
  /** The person's pin — a bind-world surface point. */
  pin: Vec3Data,
  options: {
    /** Half-thickness of the slab about the station's plane, world units (the driver passes ~8% of the forearm). */
    readonly slabHalf: number;
    /**
     * Lateral tolerance about the chord line as a FRACTION of the section's
     * half-width (default 0.3). Sized by the wrist itself, not by the
     * forearm: a 25-vertex ring on a 120 mm forearm has ~9 mm between
     * vertices, and a tolerance thinner than that threads between them
     * (measured on Ian, 2026-08-26). Also bounds the pin's distance from the skin.
     */
    readonly chordFraction?: number;
    /** Link distance for the ring's connected cluster, world units (default 2.5 × slabHalf). */
    readonly linkDistance?: number;
  },
): WristSeatUnderPin {
  const axisLength = vec3Length(axis);
  if (!(axisLength > 0)) throw new WristStationRefusal('the limb axis has no direction — parent and child are co-located');
  const unit = vec3Scale(axis, 1 / axisLength);
  const inPlane = (p: Vec3Data): Vec3Data => {
    const d = vec3Sub(p, station);
    return vec3Sub(d, vec3Scale(unit, vec3Dot(d, unit)));
  };

  // --- the slab: this station's cross-section, in the plane, CONNECTED to the click ---
  // The plane is infinite; a hand hanging beside a thigh (A-pose) puts the
  // thigh in it. The wrist ring is the connected cluster of slab points the
  // click belongs to: flood from the point nearest the pin, linking points
  // within `link` (a fraction of the slab width scaled to the forearm; a
  // low-poly ring has ~9 mm between vertices on a 120 mm forearm). Foreign
  // flesh across a gap wider than that never joins.
  const pinP = inPlane(pin);
  const all: Vec3Data[] = [];
  for (const v of vertices) {
    if (Math.abs(vec3Dot(vec3Sub(v, station), unit)) <= options.slabHalf) all.push(inPlane(v));
  }
  if (all.length < 12) {
    throw new WristStationRefusal(`the station slab holds ${all.length} vertices — too few to read a cross-section`);
  }
  const configuredLink = options.linkDistance ?? 2.5 * options.slabHalf;
  let seedIndex = 0;
  let seedDist = Infinity;
  all.forEach((p, i) => {
    const d = vec3Length(vec3Sub(p, pinP));
    if (d < seedDist) {
      seedDist = d;
      seedIndex = i;
    }
  });
  // The ring's own spacing decides, never the limb's length alone (see
  // `adaptiveLinkDistance`): a short forearm otherwise gets a link narrower
  // than its own vertex gaps and the cluster collapses to the seed.
  const link = adaptiveLinkDistance(all, seedIndex, configuredLink);
  // Flood over a spatial hash (cell = link): each pop scans only the 27
  // neighbouring cells, so a production body whose whole-scene slab holds
  // thousands of vertices no longer costs Θ(n²) per hand (Codex P2, #111).
  const cellOf = (p: Vec3Data): string => `${Math.floor(p.x / link)},${Math.floor(p.y / link)},${Math.floor(p.z / link)}`;
  const buckets = new Map<string, number[]>();
  for (let j = 0; j < all.length; j += 1) {
    const key = cellOf(all[j]!);
    const bucket = buckets.get(key);
    if (bucket === undefined) buckets.set(key, [j]);
    else bucket.push(j);
  }
  const inCluster = new Uint8Array(all.length);
  // A vertex is CONSUMED from its bucket the moment it joins the cluster, so no
  // later pop can rescan it: without that, a dense wrist piles hundreds of
  // vertices into the same few cells and every pop re-walks them all — still
  // quadratic in local density, just over a smaller set (Codex P2, #111).
  const consume = (idx: number): void => {
    const bucket = buckets.get(cellOf(all[idx]!));
    if (bucket === undefined) return;
    const at = bucket.indexOf(idx);
    if (at >= 0) {
      bucket[at] = bucket[bucket.length - 1]!;
      bucket.pop();
    }
  };
  inCluster[seedIndex] = 1;
  consume(seedIndex);
  const queue = [seedIndex];
  while (queue.length > 0) {
    const i = queue.pop()!;
    const p = all[i]!;
    const cx = Math.floor(p.x / link);
    const cy = Math.floor(p.y / link);
    const cz = Math.floor(p.z / link);
    for (let dx = -1; dx <= 1; dx += 1) {
      for (let dy = -1; dy <= 1; dy += 1) {
        for (let dz = -1; dz <= 1; dz += 1) {
          const bucket = buckets.get(`${cx + dx},${cy + dy},${cz + dz}`);
          if (bucket === undefined) continue;
          // Backwards + swap-pop: the entry swapped down was already examined
          // against this p in this pass and was out of range, so skipping it
          // here is correct, and it stays available for later pops.
          for (let bi = bucket.length - 1; bi >= 0; bi -= 1) {
            const j = bucket[bi]!;
            if (vec3Length(vec3Sub(p, all[j]!)) > link) continue;
            inCluster[j] = 1;
            bucket[bi] = bucket[bucket.length - 1]!;
            bucket.pop();
            queue.push(j);
          }
        }
      }
    }
  }
  const slab = all.filter((_, i) => inCluster[i] === 1);
  if (slab.length < 12) {
    throw new WristStationRefusal(
      `the wrist ring connected to the pin holds ${slab.length} vertices — too few to read a cross-section ` +
        `(linked at ${(link * 1000).toFixed(1)} mm over ${all.length} slab vertices; the pin may be on a detached piece)`,
    );
  }

  // --- principal axes of the section: wide (across) and thin (through) ---
  const c = vec3Scale(slab.reduce((s, p) => vec3Add(s, p), { x: 0, y: 0, z: 0 }), 1 / slab.length);
  // Two orthonormal in-plane basis vectors, then the 2×2 covariance in them.
  const seed = Math.abs(unit.x) < 0.9 ? { x: 1, y: 0, z: 0 } : { x: 0, y: 1, z: 0 };
  const e1raw = vec3Sub(seed, vec3Scale(unit, vec3Dot(seed, unit)));
  const e1 = vec3Scale(e1raw, 1 / vec3Length(e1raw));
  const e2 = { x: unit.y * e1.z - unit.z * e1.y, y: unit.z * e1.x - unit.x * e1.z, z: unit.x * e1.y - unit.y * e1.x };
  let sxx = 0;
  let sxy = 0;
  let syy = 0;
  for (const s of slab) {
    const d = vec3Sub(s, c);
    const a = vec3Dot(d, e1);
    const b = vec3Dot(d, e2);
    sxx += a * a;
    sxy += a * b;
    syy += b * b;
  }
  const theta = 0.5 * Math.atan2(2 * sxy, sxx - syy); // major axis angle in the (e1, e2) basis
  const wide = vec3Add(vec3Scale(e1, Math.cos(theta)), vec3Scale(e2, Math.sin(theta)));
  const thin = vec3Add(vec3Scale(e1, -Math.sin(theta)), vec3Scale(e2, Math.cos(theta)));
  let halfWidth = 0;
  for (const s of slab) halfWidth = Math.max(halfWidth, Math.abs(vec3Dot(vec3Sub(s, c), wide)));
  const chordRadius = (options.chordFraction ?? 0.3) * halfWidth;
  let nearest = Infinity;
  for (const s of slab) nearest = Math.min(nearest, vec3Length(vec3Sub(s, pinP)));
  if (nearest > chordRadius) {
    throw new WristStationRefusal(
      `the wrist pin sits ${nearest.toExponential(2)} off the skin of this station's slab — it is not on this wrist (re-click the crease)`,
    );
  }

  // --- the cast: along the thin axis at the pin's lateral position; a side click casts toward the centroid ---
  const pinAcross = vec3Dot(vec3Sub(pinP, c), wide);
  const sideClick = halfWidth > 0 && Math.abs(pinAcross) > 0.7 * halfWidth;
  let dir: Vec3Data;
  let cast: WristSeatUnderPin['cast'];
  if (sideClick) {
    const toC = vec3Sub(c, pinP);
    const len = vec3Length(toC);
    if (!(len > 1e-9)) return { position: c, depth: 0, sampled: slab.length, cast: 'toward-centroid', linkUsed: link };
    dir = vec3Scale(toC, 1 / len);
    cast = 'toward-centroid';
  } else {
    const sign = vec3Dot(vec3Sub(c, pinP), thin) >= 0 ? 1 : -1;
    dir = vec3Scale(thin, sign);
    cast = 'thin-axis';
  }
  let far = 0;
  let sampled = 0;
  for (const s of slab) {
    const d = vec3Sub(s, pinP);
    const t = vec3Dot(d, dir);
    if (t <= 0) continue;
    const lateral = vec3Length(vec3Sub(d, vec3Scale(dir, t)));
    if (lateral > chordRadius) continue;
    sampled += 1;
    if (t > far) far = t;
  }
  if (sampled === 0 || !(far > 0)) {
    throw new WristStationRefusal('the wrist pin casts through no flesh — no far wall within the station slab (re-click the crease)');
  }
  return { position: vec3Add(station, vec3Add(pinP, vec3Scale(dir, far / 2))), depth: far, sampled, cast, linkUsed: link };
}
